import Link from "next/link";
import { type ReactNode } from "react";

import {
  BUTTON_SIZE_CLASSES,
  BUTTON_VARIANT_CLASSES,
  type ButtonSize,
  type ButtonVariant,
} from "@/blueprint/components/Button/ButtonVariant";
import { cn } from "@/lib/utils";
import { PRESS_SCALE_CLASSES } from "@/blueprint/tokens/Motion";

type ButtonBaseProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  onClick?: () => void;
  className?: string;
  children: ReactNode;
};

type ButtonProps = ButtonBaseProps &
  (
    | {
        /** Renders a link when set; a button otherwise. */
        href: string;
        disabled?: never;
        type?: never;
      }
    | {
        href?: undefined;
        disabled?: boolean;
        type?: "button" | "submit";
      }
  );

const Button = ({
  variant = "solid",
  size = "md",
  href,
  onClick,
  disabled,
  type = "button",
  className,
  children,
}: ButtonProps) => {
  const classes = cn(
    "inline-flex items-center justify-center rounded-full",
    /* Colour settles over `quick`; the press is faster and immediate. Figma
       gives On Click the same colours as Hover, so scale is the only thing
       that reads as "pressed" — which is why `transform` is transitioned
       here and retimed under `active:` rather than left to snap. */
    "transition-[color,background-color,border-color,box-shadow,transform] duration-quick ease-out-strong",
    PRESS_SCALE_CLASSES,
    "motion-reduce:transition-none",
    BUTTON_VARIANT_CLASSES[variant],
    BUTTON_SIZE_CLASSES[size],
    className,
  );

  if (href !== undefined) {
    return (
      <Link
        href={href}
        className={classes}
        {...(onClick !== undefined ? { onClick } : {})}
      >
        {children}
      </Link>
    );
  }
  return (
    <button
      type={type}
      className={classes}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
};

export default Button;
